import { uuidModule } from "@/modules/uuid";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { OptimisticSyncUpdate, SyncModelData, SyncModelKind } from "@/store/sync/types";
import { UploadedFileBatchModelData } from "@/store/uploaded-files/types";
import { DateTime } from "luxon";
import { UploadedFileBatchObservable } from "@/store/uploaded-files/UploadedFileBatchObservable";
import { Maybe } from "@/domains/common/types";
import { IUpdateUploadedFileBatchOperation } from "@/store/sync/operations/types";

export class UpdateUploadedFileBatchOperation extends BaseSyncOperation<IUpdateUploadedFileBatchOperation> {
  batch: Maybe<UploadedFileBatchObservable>;

  get operationKind() {
    return "UPDATE_UPLOADED_FILE_BATCH" as const;
  }

  async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const optimisticUpdates: OptimisticSyncUpdate<SyncModelData>[] = [];

    const batchUpdate = this.generateUploadedFileBatchUpdate();
    if (batchUpdate) {
      optimisticUpdates.push(batchUpdate);
    }

    return optimisticUpdates;
  }

  protected generateUploadedFileBatchUpdate(): OptimisticSyncUpdate<UploadedFileBatchModelData> {
    const now = DateTime.now().toISO();
    const batch = this.batch;

    if (!batch) {
      throw new Error(`Batch ${this.payload.id} not found`);
    }

    return {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: this.payload.id,
        model_kind: SyncModelKind["UploadedFileBatch"],
        model_version: batch.modelVersion,
        model_data: {
          owned_by_space_account_id: batch.modelData.owned_by_space_account_id,
          locally_created_at: batch.modelData.locally_created_at,
          locally_modified_at: now,
          config_json: this.payload.config_json as Record<string, unknown>,
          processing_enqueued_at: batch.modelData.processing_enqueued_at,
          processing_started_at: batch.modelData.processing_started_at,
          processing_ended_at: batch.modelData.processing_ended_at,
          associated_session_id: batch.modelData.associated_session_id,
        },
        model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
      },
    };
  }

  async triggerRecompute(): Promise<void> {
    await this.store.uploadedFileBatches.recompute(this.payload.id);
  }

  async execute(): Promise<void> {
    this.batch = await this.store.uploadedFileBatches.getAsync(this.payload.id);
    return super.execute();
  }
}
